import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { useAppDispatch } from "store/hooks";
import { handleGetDealerDriversRequest } from "store/modules/DealerUsers/thunk.store";
import { DealerUsersProps } from "store/modules/DealerUsers/types.store";
import { handleGetCoverageAreaOrderDetailRequest } from "store/modules/Logistics/thunk.store";
import { handleAddDriveOrderRequest } from "store/modules/Orders/thunk.store";
import { CoverageAreaOrderDetailDto } from "types/coverageAreaOrderDetail";

import {
  buildDriverSelectOptions,
  collectListedDriverIds,
} from "../OrderDriverAutocomplete/buildDriverSelectOptions";
import {
  getDealerDriversInsight,
  mapDriverInsightsById,
} from "services/getDealerDriversInsight";
import type { DriverInsightSnapshot } from "types/dealerDriverInsight";
import { LOGISTICS_DRIVER_STATUS_ONLINE } from "types/dealerDriver";
import {
  DriverSelectOption,
  NO_DRIVER_OPTION_ID,
} from "../OrderDriverAutocomplete/types";

interface UseOrderDriverSelectionParams {
  dealerId: string;
  orderId: string;
  coverageAreaId?: string;
}

export interface UseOrderDriverSelectionResult {
  isSelecting: boolean;
  loadingDrivers: boolean;
  hasDriverInsights: boolean;
  driverOptions: DriverSelectOption[];
  selectedDriver: DriverSelectOption | null;
  setSelectedDriver: (option: DriverSelectOption | null) => void;
  openSelection: () => void;
  closeSelection: () => void;
  applySelection: () => Promise<void>;
}

const DEALER_DRIVERS_PAGE = { limit: 200 };

export function useOrderDriverSelection({
  dealerId,
  orderId,
  coverageAreaId,
}: UseOrderDriverSelectionParams): UseOrderDriverSelectionResult {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [allUsers, setAllUsers] = useState<DealerUsersProps[] | undefined>();
  const [coverageAreaDetail, setCoverageAreaDetail] =
    useState<CoverageAreaOrderDetailDto | null>(null);
  const [driverInsightsById, setDriverInsightsById] = useState<
    Map<string, DriverInsightSnapshot>
  >(new Map());
  const [hasDriverInsights, setHasDriverInsights] = useState(false);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [selectedDriver, setSelectedDriver] =
    useState<DriverSelectOption | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const loadSequenceRef = useRef(0);

  const driverOptions = useMemo(
    () =>
      buildDriverSelectOptions({
        coverageAreaDetail,
        dealerUsers: allUsers,
        driverInsightsById,
        t,
      }),
    [coverageAreaDetail, allUsers, driverInsightsById, t],
  );

  const loadSelectionData = useCallback(async () => {
    const sequence = loadSequenceRef.current + 1;
    loadSequenceRef.current = sequence;
    const isStale = (): boolean => sequence !== loadSequenceRef.current;

    setLoadingDrivers(true);
    setCoverageAreaDetail(null);
    setAllUsers(undefined);
    setDriverInsightsById(new Map());
    setHasDriverInsights(false);

    let loadedCoverageAreaDetail: CoverageAreaOrderDetailDto | null = null;
    let loadedDealerUsers: DealerUsersProps[] = [];

    try {
      if (coverageAreaId) {
        loadedCoverageAreaDetail = await dispatch(
          handleGetCoverageAreaOrderDetailRequest({
            dealerId,
            coverageAreaId,
          }),
        )
          .unwrap()
          .catch((): CoverageAreaOrderDetailDto | null => {
            if (isStale()) return null;

            toast(t("failedLoadCoverageAreaDrivers").toString(), {
              type: "error",
              autoClose: false,
            });
            return null;
          });

        if (isStale()) return;

        setCoverageAreaDetail(loadedCoverageAreaDetail);
      } else {
        setCoverageAreaDetail(null);
      }

      const usersResponse = await dispatch(
        handleGetDealerDriversRequest({
          dealerId,
          suppressGlobalSpinner: true,
          statuses: [LOGISTICS_DRIVER_STATUS_ONLINE],
          ...DEALER_DRIVERS_PAGE,
        }),
      ).unwrap();

      if (isStale()) return;

      loadedDealerUsers = usersResponse.data ?? [];
      setAllUsers(loadedDealerUsers);

      const driverIds = collectListedDriverIds(
        loadedCoverageAreaDetail,
        loadedDealerUsers,
      );

      if (driverIds.length > 0) {
        try {
          const insights = await getDealerDriversInsight({
            dealerId,
            driverIds,
          });

          if (isStale()) return;

          setDriverInsightsById(mapDriverInsightsById(insights));
          setHasDriverInsights(true);
        } catch {
          if (isStale()) return;

          setDriverInsightsById(new Map());
          setHasDriverInsights(false);
        }
      }
    } catch {
      if (isStale()) return;

      toast(t("failedLoadDealerDrivers").toString(), {
        type: "error",
        autoClose: false,
      });
    } finally {
      if (!isStale()) {
        setLoadingDrivers(false);
      }
    }
  }, [coverageAreaId, dealerId, dispatch, t]);

  const openSelection = useCallback(() => {
    setIsSelecting(true);
    setSelectedDriver(null);
    loadSelectionData().catch(() => undefined);
  }, [loadSelectionData]);

  const closeSelection = useCallback(() => {
    loadSequenceRef.current += 1;
    setIsSelecting(false);
    setSelectedDriver(null);
    setLoadingDrivers(false);
    setCoverageAreaDetail(null);
    setAllUsers(undefined);
    setDriverInsightsById(new Map());
    setHasDriverInsights(false);
  }, []);

  const applySelection = useCallback(async (): Promise<void> => {
    if (!selectedDriver) return;

    const isClearDriver = selectedDriver.id === NO_DRIVER_OPTION_ID;

    const result = await dispatch(
      handleAddDriveOrderRequest({
        dealerId,
        orderId,
        ...(isClearDriver
          ? { clearDriver: true }
          : {
              driverId: selectedDriver.id,
              driverName: selectedDriver.nameForAssignment,
            }),
      }),
    );

    if (handleAddDriveOrderRequest.fulfilled.match(result)) {
      closeSelection();
    }
  }, [dispatch, dealerId, orderId, selectedDriver, closeSelection]);

  return {
    isSelecting,
    loadingDrivers,
    hasDriverInsights,
    driverOptions,
    selectedDriver,
    setSelectedDriver,
    openSelection,
    closeSelection,
    applySelection,
  };
}
